import { useEffect, useId, useRef, useState } from "react";
import { IonCheckbox, IonModal, IonSpinner } from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import { Subject, SubjectResponse } from "model";
import { Card, Colors } from "swing-components";

import { fetchRequestAttribute, translate } from "~utils";

import { ModalHeader } from "../ModalHeader";
import { SelectItem } from "../SelectItem";

type SubjectFieldProps = {
  /**
   * List of Subject `value` field that are selected
   */
  subjectValues: string[];
  /**
   * Handler when the user selects a Subject. Only the `value` of the Subject
   * will be passed to the handler.
   */
  onChange: (subjects: Array<string>) => void;
  /**
   * Whether the SubjectField is disabled.
   */
  isDisabled?: boolean;
};

export function SubjectField(props: SubjectFieldProps) {
  const { subjectValues = [], onChange, isDisabled } = props;

  /*** Queries ***/
  const { data: subjects } = useQuery({
    queryKey: ["fetchRequestAttributeSubjects"],
    queryFn: async () =>
      (await fetchRequestAttribute<SubjectResponse>({ key: "subjects" })).data.options,
  });

  /*** Refs ***/
  const modalId = useId();
  const subjectModal = useRef<HTMLIonModalElement>(null);

  /*** States ***/
  const [searchTerm, setSearchTerm] = useState("");
  const [tempSubjectValues, setTempSubjectValues] = useState<string[]>(subjectValues);

  /*** Effects ***/
  // When Subject data response comes in and there are `tempSubjectValues`,
  // get the selected Subjects.

  /**
   * When the `subjects` or `subjectValues` change, call the
   * `getSelectedSubject` function to handle cases where there is un-matched
   * `tempSubjectValues` to the `subjects`.
   *
   * This is to handle the case that the API, or `subjectsValue` given no longer
   * match in which case we will select the `Unknown` Subject.
   */
  useEffect(() => {
    // Bail when there are no subjects or `tempSubjectValues`
    if (!subjects || tempSubjectValues.length === 0) return;

    // Get the selected Subjects
    const selectedSubjects = getSelectedSubject({ subjects, subjectValues: tempSubjectValues });

    // Set the `tempSubjectValues` to the selected Subjects
    setTempSubjectValues(selectedSubjects.map((s) => s.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjects, subjectValues]);

  /*** Constants ***/
  // The list of Subjects after search string filtering and sorting based on
  // the `order` field.
  const filteredSubjects = subjects
    ?.filter((subject: Subject) => subject.label.toLowerCase().includes(searchTerm.toLowerCase()))
    .toSorted((a, b) => a.order - b.order);

  /*** Handlers ***/
  function handleModalClose() {
    // Reset the search term
    setSearchTerm("");
    // Reset the selected subjects to be the initial subjects
    setTempSubjectValues(getSelectedSubject({ subjects, subjectValues }).map((s) => s.value));
    // Close the modal
    subjectModal.current?.dismiss();
  }

  function handleModalConfirm() {
    // Reset the search term
    setSearchTerm("");
    // Pass the selected subjects to the parent
    onChange(tempSubjectValues);
    // Close the modal
    subjectModal.current?.dismiss();
  }

  function handleChange(params: { checked: boolean; value: string }) {
    const { checked, value } = params;

    if (checked) setTempSubjectValues((tempSubjectValues) => [...tempSubjectValues, value]);
    else setTempSubjectValues(tempSubjectValues.filter((item) => item !== value));
  }

  /**
   * If "Unknown" is selected, disable all other Subjects.
   * If anything other than "Unknown" is selected, disabled the "Unknown" Subject.
   * Otherwise don't disable any Subject.
   */
  function isSubjectDisabled(subject: Subject) {
    if (tempSubjectValues.length === 0) return false;

    if (tempSubjectValues.includes("Unknown") && subject.value !== "Unknown") {
      return true;
    }

    if (!tempSubjectValues.includes("Unknown") && subject.value === "Unknown") {
      return true;
    }

    return false;
  }

  /*** Render ***/
  return (
    <SelectItem
      id={modalId}
      label={translate("LABEL_SUBJECT")}
      // Show the selected subjects `label` field joined by a comma
      value={getSelectedSubject({ subjects, subjectValues })
        .map((s) => s.label)
        .join(", ")}
      stacked
      isDisabled={isDisabled}
      css={isDisabled ? { color: Colors.slate300, cursor: "not-allowed" } : undefined}
    >
      <IonModal
        trigger={modalId}
        ref={subjectModal}
        aria-label="Subject Selection"
        backdropDismiss
        onDidDismiss={handleModalClose}
        className="modal-600 stackedModal"
      >
        <ModalHeader
          title={translate("SUBJECT_MODAL_HEADER_LABEL")}
          onModalDismiss={handleModalClose}
          onModalConfirm={handleModalConfirm}
          onIonInput={(e) => setSearchTerm(e?.target?.value || "")}
        />

        {/* Modal Content */}
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            padding: 16,
            overflow: "auto",
            height: "100%",
            backgroundColor: Colors.white300,
          }}
        >
          <Card>
            {/* When loading the Subjects data, show a loading spinner */}
            {subjects === undefined && (
              <div css={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
                <IonSpinner name="dots" />
              </div>
            )}

            {/* When there are Subjects, show the Subjects */}
            {filteredSubjects && filteredSubjects.length > 0 && (
              <>
                {filteredSubjects.map((subject: Subject, index) => {
                  const isFirst = index === 0;
                  const isLast = index === filteredSubjects.length - 1;

                  return (
                    <IonCheckbox
                      key={subject.value}
                      labelPlacement="end"
                      justify="start"
                      value={subject.value}
                      checked={tempSubjectValues.includes(subject.value)}
                      onIonChange={(e) => handleChange(e.detail)}
                      disabled={isSubjectDisabled(subject)}
                      css={{
                        width: "100%",
                        paddingTop: isFirst ? 0 : 16,
                        paddingBottom: isLast ? 0 : 16,
                        // Always include boxShadow key for readability
                        boxShadow: isLast ? "none" : `inset 0 -1px 0 ${Colors.slate200}`,
                      }}
                    >
                      {subject.label}
                    </IonCheckbox>
                  );
                })}
              </>
            )}

            {/* When there are no Subjects, show a message */}
            {filteredSubjects && filteredSubjects.length === 0 && (
              <div css={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
                <p>No subjects found</p>
              </div>
            )}
          </Card>
        </div>
      </IonModal>
    </SelectItem>
  );
}

/***** Helpers Functions *****/
/**
 * Given a list of Subjects and a list of subject values, return the list of
 * Subjects that match the subject values.
 *
 * If there are any non-matching `subjectValue`, then return the Unknown Subject.
 */
function getSelectedSubject(params: {
  subjects?: Subject[];
  subjectValues: string[];
}): Array<Subject> {
  const { subjects, subjectValues } = params;

  // If there are no subjects, or subject values, then return an empty array
  if (subjects === undefined || subjectValues.length === 0) return [];

  // Create a list of selected subjects
  const selectedSubjects = subjects.filter((subject: Subject) =>
    subjectValues.includes(subject.value),
  );

  // If there is any non-matching `subjectValue`
  if (selectedSubjects.length !== subjectValues.length) {
    // Find the Unknown Subject from the list of Subjects
    const unknownSubject = getUnknownSubject(subjects);

    // If the Unknown Subject is found, then add it to the list of selected subjects
    if (unknownSubject !== undefined) {
      return [unknownSubject];
    }

    // Otherwise return an empty array
    return [];
  }

  // Otherwise return the list of selected subjects
  return selectedSubjects;
}

/**
 * Get the Unknown Subject from the list of Subjects.
 * @returns The Unknown Subject or undefined if it is not found.
 */
function getUnknownSubject(subjects: Subject[]) {
  const unknownSubject = subjects.find((s) => s.value === "Unknown");

  if (unknownSubject === undefined) {
    // Send a console error to notify the engineers as when this code was
    // written there was a `Unknown` Subject.
    // eslint-disable-next-line no-console
    console.error("SubjectField: Subject with `value` of `Unknown` not found.");
  }

  return unknownSubject;
}
